/**
 * Units repository — the ONLY place that touches `units` data.
 *
 * Hooks (`useUnits`, `useUnit`, …) and components call these functions; nothing
 * else may call `supabase.from("units")` directly. Follows the Strangler-Fig
 * per-slice migration template (see `properties.ts` + the handoff doc).
 *
 * Every read/write is company-scoped. The employee Layer-2 scope is the optional
 * `assignedPropertyIds` filter, computed by the caller from
 * `employee_property_assignments` (a different resource).
 *
 * Note: `delete` is a SOFT delete (`is_active = false`) — units are never hard
 * deleted, preserving contract/finance history that references them.
 */
import { unitRowSchema, unitStatsSchema } from "@dallali/shared";
import { supabase } from "@/integrations/supabase/client";
import type { Database, TablesInsert, TablesUpdate } from "@/integrations/supabase/types";
import { fireAndForgetNestPost, makeNestRequest } from "@/services/nestRequest";
import { API_ROUTES } from "@/api/routes";
import { API_URL } from "@/services/api";
import {
  isDefinitiveNotFound,
  reportNestFallback,
  reportNestFallbackOrRethrowUnknownCommit,
} from "@/lib/nestFallback";
import { OWNER_LITE_COLUMNS, type OwnerLite } from "@/lib/ownerHelpers";

const TABLE = "units";

// Authenticated Nest request bound to the "units" slice — shared transport.
// UNSCOPED: no x-active-role forwarded/required. Used by writes (create/update/
// softDelete) — WRITE endpoints do NOT carry RowScopeGuard (capability-gated
// only, see units.controller.ts), so they must never be blocked or rerouted to
// the Supabase fallback by a missing/unresolved active role (issue #3278 —
// a client-side guard on a shared instance previously did exactly that).
const nestRequest = makeNestRequest("units");

// Row-scoped variant: /units list/getById/stats ARE RowScopeGuard-protected and
// 403 without x-active-role — mirror the Supabase client's per-call header, and
// short-circuit the boot-race window client-side (`requireActiveRole`).
const nestRequestScoped = makeNestRequest("units", {
  forwardActiveRole: true,
  requireActiveRole: true,
});
/** Canonical unit detail select — full row + owner.  Used by getByIdViaSupabase. */
const OWNER_SELECT = `*, owner:owners(${OWNER_LITE_COLUMNS})`;

/**
 * Unit list select — omits heavy columns not consumed by any list view
 * (amenities JSONB, free-text descriptions, translation metadata).
 * Backs listViaSupabase only; getByIdViaSupabase keeps the full OWNER_SELECT.
 */
const UNIT_LIST_SELECT = `id, unit_number, unit_type, status, property_id, area, bedrooms, bathrooms, living_rooms, floor_number, rent_amount, sale_price, annual_rent, deposit_amount, is_furnished, is_active, owner_id, company_id, key_type, key_number, electricity_meter, water_meter, images, purpose, created_at, owner:owners(${OWNER_LITE_COLUMNS})`;

/** Full unit list row type — DB Row + embedded owner.  The projection is narrower
 *  at runtime; the as-cast in listViaSupabase widens back so consumers typed
 *  against UnitRow (useUnits, UnitCardEnhanced, PropertyUnitsListView, …)
 *  type-check without seeing the projection gap. */
type UnitListRow = Database["public"]["Tables"]["units"]["Row"] & { owner: OwnerLite | null };

/** Narrow unit row for the task property-context sidebar (fe-tail T9),
 *  including the legacy inline key columns (`key_type`/`key_number`) the hook
 *  falls back to when no `security_keys` row exists for the unit. */
export interface TaskContextUnitRow {
  id: string;
  unit_number: string;
  floor_number: number | null;
  bedrooms: number | null;
  bathrooms: number | null;
  key_type: string | null;
  key_number: string | null;
  amenities: unknown;
  rent_amount: number | null;
  annual_rent: number | null;
  property_id: string | null;
  images: unknown;
}

interface ListOptions {
  companyId: string;
  /** Restrict to one property's units. */
  propertyId?: string;
  /**
   * Employee Layer-2 scope. `null`/`undefined` → not an employee, no filter.
   * A non-empty array → restrict to units of those properties.
   */
  assignedPropertyIds?: string[] | null;
}

/** Supabase list implementation — fallback for the flipped path. */
async function listViaSupabase({ companyId, propertyId, assignedPropertyIds }: ListOptions) {
  let query = supabase
    .from(TABLE)
    .select(UNIT_LIST_SELECT)
    .eq("company_id", companyId)
    .eq("is_active", true)
    .order("unit_number", { ascending: true });

  if (propertyId) {
    query = query.eq("property_id", propertyId);
  }
  if (assignedPropertyIds && assignedPropertyIds.length > 0) {
    query = query.in("property_id", assignedPropertyIds);
  }

  const { data, error } = await query;
  if (error) throw error;
  // SELECT is a projection subset — cast widens back so consumers with
  // UnitRow-inferred types type-check without seeing the projection gap.
  return data as unknown as UnitListRow[];
}

/** Supabase getById implementation — fallback for the flipped path. */
async function getByIdViaSupabase(id: string, companyId: string) {
  const { data, error } = await supabase
    .from(TABLE)
    .select(OWNER_SELECT)
    .eq("id", id)
    .eq("company_id", companyId)
    .maybeSingle();
  if (error) throw error;
  return data;
}

async function createViaSupabase(payload: TablesInsert<"units">) {
  const { data, error } = await supabase
    .from(TABLE)
    .insert(payload)
    .select()
    .maybeSingle();
  if (error) throw error;
  // An insert that returns no row is abnormal — surface it rather than
  // handing the caller undefined (and avoid .single()'s PGRST116 throw).
  if (!data) throw new Error("units insert returned no row");
  return data;
}

/**
 * Supabase update implementation — fallback for the flipped path. Keeps the
 * RLS-silent-block detection (0-row update → descriptive error with
 * `cause === "RLS_SILENT_BLOCK_OR_NOT_FOUND"`).
 */
async function updateViaSupabase(
  id: string,
  patch: TablesUpdate<"units">,
  companyId: string,
) {
  const { data, error } = await supabase
    .from(TABLE)
    .update(patch)
    .eq("id", id)
    .eq("company_id", companyId)
    .select()
    .maybeSingle();
  if (error) throw error;
  if (data === null) {
    const err = new Error(
      "لم يتم حفظ التغييرات — تحقق من صلاحياتك أو حاول مجدداً. " +
        "Changes were not saved — check your permissions or try again.",
    );
    (err as Error & { cause: string }).cause = "RLS_SILENT_BLOCK_OR_NOT_FOUND";
    throw err;
  }
  return data;
}

export const unitsRepository = {
  /**
   * Active company-scoped units, ordered by unit_number; optionally
   * property/employee scoped.
   *
   * FLIPPED to the guarded Nest endpoint (Track-E slice 3: GET /units —
   * enables eventual RLS retirement for this read). The Nest path accepts an
   * optional `?property_id=` query param (mirrors the Supabase `propertyId`
   * filter) and relies on the forwarded `x-active-role` header for employee
   * row-scope (the RowScopeGuard handles `assignedPropertyIds` server-side,
   * so no extra query param is needed for that case).
   *
   * Returns the same shape as the Supabase UNIT_LIST_SELECT. Supabase is the loud
   * fallback (reversible unit).
   */
  async list({ companyId, propertyId, assignedPropertyIds }: ListOptions) {
    if (API_URL) {
      try {
        const qs = propertyId ? `?property_id=${encodeURIComponent(propertyId)}` : "";
        const rows = await nestRequestScoped<unknown[]>(
          `${API_ROUTES.units.list()}${qs}`,
        );
        return unitRowSchema
          .array()
          .parse(rows) as unknown as UnitListRow[];
      } catch (err) {
        reportNestFallback("units.list", err);
      }
    }
    return listViaSupabase({ companyId, propertyId, assignedPropertyIds });
  },

  /**
   * Single unit by id (with owner embed), company-scoped.
   *
   * FLIPPED to the guarded Nest endpoint (Track-E: GET /units/:id —
   * enables eventual RLS retirement for this read). Returns the same shape as
   * the Supabase OWNER_SELECT (`*, owner:owners(id, full_name, full_name_en)`).
   * Supabase remains the loud fallback (reversible unit).
   */
  async getById(id: string, companyId: string) {
    if (API_URL) {
      try {
        const row = await nestRequestScoped<unknown>(API_ROUTES.units.getById(id));
        return unitRowSchema.parse(row) as unknown as Awaited<ReturnType<typeof getByIdViaSupabase>>;
      } catch (err) {
        // Deterministic 404 = the unit is genuinely absent / soft-deleted /
        // cross-tenant / row-scoped out. The Supabase fallback (company_id
        // filter only, no row-scope) would return null anyway — answer null
        // directly, no fallback round-trip, no nest-fallback noise (#3395).
        if (isDefinitiveNotFound(err)) {
          return null as Awaited<ReturnType<typeof getByIdViaSupabase>>;
        }
        reportNestFallback("units.getById", err);
      }
    }
    return getByIdViaSupabase(id, companyId);
  },

  /**
   * Insert a unit; returns the row.
   *
   * FLIPPED to the guarded Nest endpoint (cache-layer Phase 2 write-ownership
   * gate — the parent property's cached card invalidates inside the Nest write
   * path). `company_id` is derived from the JWT server-side; Supabase remains
   * the loud fallback.
   *
   * NON-IDEMPOTENT write → duplicate-write guard (#3316 / #3399): the Supabase
   * fallback only runs when Nest verifiably did not commit (safe 4xx /
   * unconfigured). An ambiguous outcome (network, timeout, 5xx, parse failure
   * after a 2xx) RETHROWS instead of re-issuing the insert — a fallback after a
   * committed-but-lost Nest write would double-create the unit.
   */
  async create(payload: TablesInsert<"units">) {
    if (API_URL) {
      try {
        const row = await nestRequest<unknown>(API_ROUTES.units.create(), {
          method: "POST",
          body: JSON.stringify(payload),
        });
        return unitRowSchema.parse(row) as unknown as Awaited<
          ReturnType<typeof createViaSupabase>
        >;
      } catch (err) {
        reportNestFallbackOrRethrowUnknownCommit("units.create", err);
      }
    }
    return createViaSupabase(payload);
  },

  /**
   * Update a company-scoped unit; returns the updated row.
   *
   * Throws a descriptive error when the update matches 0 rows — this can happen
   * when the unit genuinely does not exist, BUT also when RLS silently blocks the
   * write because the caller's `x-active-role` header maps to a role that lacks
   * the required capability (e.g. `units.edit`). Supabase returns HTTP 200 with
   * an empty result in that case — without this guard the caller would believe the
   * save succeeded while the database was never mutated.
   *
   * Callers that need to distinguish the two failure modes can inspect:
   *   `(error as { cause?: string }).cause === "RLS_SILENT_BLOCK_OR_NOT_FOUND"`
   */
  async update(id: string, patch: TablesUpdate<"units">, companyId: string) {
    if (API_URL) {
      try {
        const row = await nestRequest<unknown>(API_ROUTES.units.update(id), {
          method: "PUT",
          body: JSON.stringify(patch),
        });
        return unitRowSchema.parse(row) as unknown as Awaited<
          ReturnType<typeof updateViaSupabase>
        >;
      } catch (err) {
        reportNestFallback("units.update", err);
      }
    }
    return updateViaSupabase(id, patch, companyId);
  },

  /**
   * Bulk-insert unit rows (RER import — every row already carries its
   * company_id/owner_id from buildUnitRowsWithDefault). Runs under the
   * CALLER's session (owner-portal auto-import inserts as the owner persona,
   * per its RLS insert policy). Throws on error — both import flows handle
   * the failure locally (Sentry + warning toast, property kept).
   */
  async insertMany(
    rows: TablesInsert<"units">[],
    companyId: string,
  ): Promise<void> {
    // company_id is re-stamped per row rather than trusted from the caller —
    // the repository is the tenancy boundary for this bulk write.
    const scoped = rows.map((r) => ({ ...r, company_id: companyId }));
    const { error } = await supabase.from(TABLE).insert(scoped);
    if (error) throw error;
  },

  /**
   * Sync `purpose` across a set of units (EJAR import VAT alignment — the
   * lease's VAT treatment is EJAR's, so the units follow the contract).
   * Company-scoped. NEVER throws — the inline original discarded the result
   * (a failed sync leaves only a label mismatch, not data loss).
   */
  async updatePurposeByIds(
    unitIds: string[],
    companyId: string,
    purpose: "residential" | "commercial",
  ): Promise<void> {
    await supabase
      .from(TABLE)
      .update({ purpose } as never)
      .in("id", unitIds)
      .eq("company_id", companyId);
  },

  /**
   * Soft-delete a company-scoped unit (`is_active = false`).
   * FLIPPED to Nest (DELETE /units/:id — soft server-side, invalidates the
   * unit's cached card deps); Supabase stays the loud fallback.
   */
  async softDelete(id: string, companyId: string) {
    if (API_URL) {
      try {
        await nestRequest<unknown>(API_ROUTES.units.remove(id), {
          method: "DELETE",
        });
        return;
      } catch (err) {
        reportNestFallback("units.softDelete", err);
      }
    }
    const { error } = await supabase
      .from(TABLE)
      .update({ is_active: false })
      .eq("id", id)
      .eq("company_id", companyId);
    if (error) throw error;
  },

  /**
   * Status breakdown for active units belonging to active properties.
   * The property join + `is_active` filter mirrors the original hook so a unit
   * under a soft-deleted property is excluded from the totals.
   */
  async statsByStatus(companyId: string, range?: { startDate?: string; endDate?: string }) {
    // FLIPPED to the cached Nest summary (GET /units/stats — Phase 3); the
    // endpoint mirrors this exact breakdown. Loud Supabase fallback. A date
    // range bypasses the Nest-side cache (see UnitsService.stats) — the FE
    // just forwards it.
    if (API_URL) {
      try {
        const stats = await nestRequestScoped<unknown>(
          API_ROUTES.units.stats(range?.startDate, range?.endDate),
        );
        return unitStatsSchema.parse(stats);
      } catch (err) {
        reportNestFallback("units.statsByStatus", err);
      }
    }
    let query = supabase
      .from(TABLE)
      .select("status, property:properties!units_property_id_fkey(is_active, deleted_at)")
      .eq("company_id", companyId)
      .eq("is_active", true);
    if (range?.startDate) query = query.gte("created_at", range.startDate);
    if (range?.endDate) query = query.lte("created_at", range.endDate);
    const { data, error } = await query;
    if (error) throw error;

    // Mirror the Nest path: a soft-deleted property (deleted_at stamped,
    // is_active untouched) must not feed the stats (round-8 review).
    const filtered = data.filter(
      (u) => u.property?.is_active === true && u.property?.deleted_at == null,
    );
    return {
      total: filtered.length,
      available: filtered.filter((u) => u.status === "available").length,
      rented: filtered.filter((u) => u.status === "rented").length,
      maintenance: filtered.filter((u) => u.status === "maintenance").length,
    };
  },

  /**
   * Active unit ids for a property. NOT company-filtered — verbatim from the
   * pre-consolidation site (RLS scopes it; the follow-up contract_requests
   * read re-applies the company filter). Throws on error.
   */
  async listActiveIdsByProperty(
    propertyId: string,
  ): Promise<{ id: string }[]> {
    const { data, error } = await supabase
      .from(TABLE)
      .select("id")
      .eq("property_id", propertyId)
      .eq("is_active", true);
    if (error) throw error;
    return data ?? [];
  },

  /**
   * A property's active units with their owner context, unit_number order —
   * the property-drawer Units tab base rows. Company-scoped; throws on error.
   */
  async listWithOwnerByProperty(companyId: string, propertyId: string) {
    const { data, error } = await supabase
      .from(TABLE)
      .select(`*, owner:owners(${OWNER_LITE_COLUMNS})`)
      .eq("company_id", companyId)
      .eq("property_id", propertyId)
      .eq("is_active", true)
      .order("unit_number", { ascending: true });
    if (error) throw error;
    return data ?? [];
  },

  /**
   * One page of active-unit occupancy rows (id/status/rent/area + property
   * name) — the caller loops `range` pages of its chunk size. Company-scoped;
   * throws on error.
   */
  async pageOccupancyRows(companyId: string, from: number, to: number) {
    const { data, error } = await supabase
      .from(TABLE)
      .select(`
              id,
              unit_number,
              status,
              rent_amount,
              area,
              property_id,
              created_at,
              property:properties(name, name_en)
            `)
      .eq("company_id", companyId)
      .eq("is_active", true)
      .range(from, to);
    if (error) throw error;
    return data ?? [];
  },

  /**
   * `{ status }` of every active unit (reports-dashboard occupancy tile).
   * Company-scoped. NEVER throws: the pre-consolidation site read `res.data`
   * without an error check (tile falls back to 0%), so an error resolves
   * to [].
   */
  async listStatuses(companyId: string): Promise<{ status: string }[]> {
    const { data } = await supabase
      .from(TABLE)
      .select("status")
      .eq("company_id", companyId)
      .eq("is_active", true);
    return data ?? [];
  },

  /**
   * `{ id, property_id, status }` of every active unit (property-performance
   * report per-property occupancy). Company-scoped. NEVER throws: the
   * pre-consolidation site read `res.data` without an error check (report
   * renders empty), so an error resolves to [].
   */
  async listPerformanceRows(
    companyId: string,
  ): Promise<{ id: string; property_id: string; status: string }[]> {
    const { data } = await supabase
      .from(TABLE)
      .select("id, property_id, status")
      .eq("company_id", companyId)
      .eq("is_active", true);
    return data ?? [];
  },

  /**
   * Full unit row by id, RLS-scoped only (verbatim from the contract-details
   * page — `contract.unit_id` is already company-validated by the
   * company-scoped contract read upstream). Throws on error; null when
   * absent.
   */
  async getByIdRlsScoped(id: string) {
    const { data, error } = await supabase
      .from(TABLE)
      .select("*")
      .eq("id", id)
      .maybeSingle();
    if (error) throw error;
    return data;
  },

  /**
   * Cascade a property's purpose change onto all of its units (the
   * single-purpose flip in the edit-property dialog). Scoped by property AND
   * company; throws on error.
   */
  async updatePurposeByProperty(
    propertyId: string,
    companyId: string,
    purpose: string,
  ): Promise<void> {
    const { error } = await supabase
      .from(TABLE)
      .update({ purpose })
      .eq("property_id", propertyId)
      .eq("company_id", companyId);
    if (error) throw error;
  },

  /**
   * HEAD-only count of a property's ACTIVE units (the multi→single ownership
   * sync dialog decision). NEVER throws: the pre-consolidation site
   * destructured `{ count }` only (a failed read skips the sync dialog), so
   * an error resolves to null.
   */
  async countActiveForProperty(
    propertyId: string,
    companyId: string,
  ): Promise<number | null> {
    const { count } = await supabase
      .from(TABLE)
      .select("*", { count: "exact", head: true })
      .eq("property_id", propertyId)
      .eq("company_id", companyId)
      .eq("is_active", true);
    return count ?? null;
  },

  /**
   * Sync every unit of a property onto one owner (the multi→single ownership
   * confirmation). Scoped by property AND company. NEVER throws — verbatim
   * from the pre-consolidation site, which discarded the result entirely
   * (the follow-up cache invalidations + draft-contract propagation run
   * regardless); flip candidate for a properly-erroring Nest write.
   */
  async setOwnerByProperty(
    propertyId: string,
    companyId: string,
    ownerId: string,
  ): Promise<void> {
    await supabase
      .from(TABLE)
      .update({ owner_id: ownerId })
      .eq("property_id", propertyId)
      .eq("company_id", companyId);
  },

  /**
   * Oldest ACTIVE unit with this unit_number in a property — the gov-import
   * resolve step. Duplicate-tolerant (`.limit(1)` + oldest-first). Scoped by
   * property AND company; throws the wrapped lookup error verbatim.
   */
  async findOldestActiveByUnitNumber(
    propertyId: string,
    companyId: string,
    unitNumber: string,
  ): Promise<{ id: string } | null> {
    const { data, error } = await supabase
      .from(TABLE)
      .select("id")
      .eq("property_id", propertyId)
      .eq("company_id", companyId)
      .eq("unit_number", unitNumber)
      .eq("is_active", true)
      .order("created_at", { ascending: true })
      .limit(1)
      .maybeSingle();
    if (error) throw new Error(`Unit lookup failed: ${error.message}`);
    return data;
  },

  /**
   * Vacant/reserved active units (with property context) not currently
   * advertised — the ads wizard's unit picker. `excludeIds` are the active
   * listings' unit ids; applied as a NOT-IN only when non-empty (verbatim).
   * Company-scoped; throws on error.
   */
  async listAvailableForAd(companyId: string, excludeIds: string[]) {
    let query = supabase
      .from(TABLE)
      .select(`
          *,
          property:properties(id, name, name_en, city, district)
        `)
      .eq("company_id", companyId)
      .eq("is_active", true)
      .in("status", ["available", "reserved"]);

    if (excludeIds.length > 0) {
      query = query.not("id", "in", `(${excludeIds.join(",")})`);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  /**
   * First active unit id of a property (REGA wizard unambiguous pre-select).
   * Scoped by property AND company. NEVER throws: the pre-consolidation site
   * destructured `{ data }` only (no pre-select on failure), so an error
   * resolves to [].
   */
  async listFirstActiveIdForProperty(
    propertyId: string,
    companyId: string,
  ): Promise<{ id: string }[]> {
    const { data } = await supabase
      .from(TABLE)
      .select("id")
      .eq("property_id", propertyId)
      .eq("company_id", companyId)
      .eq("is_active", true)
      .limit(1);
    return data ?? [];
  },

  /**
   * `{ id, unit_number, unit_type }` rows for a property, unit_number order —
   * the add-payment dialog's unit dropdown. RLS-scoped only (verbatim; the
   * propertyId comes from the company-scoped property dropdown upstream).
   * Throws on error.
   */
  async listForPaymentDialog(
    propertyId: string,
  ): Promise<{ id: string; unit_number: string; unit_type: string | null }[]> {
    const { data, error } = await supabase
      .from(TABLE)
      .select("id, unit_number, unit_type")
      .eq("property_id", propertyId)
      .order("unit_number");
    if (error) throw error;
    return data || [];
  },

  /**
   * Pre-delete impact summary (`get_unit_delete_summary` RPC). RLS-scoped
   * like the pre-consolidation sites; throws on error (both callers fall
   * back to a counts-less dialog). Result passed through unparsed.
   */
  async getDeleteSummary(unitId: string): Promise<unknown> {
    const { data, error } = await supabase.rpc("get_unit_delete_summary", {
      p_unit_id: unitId,
    });
    if (error) throw error;
    return data;
  },

  /**
   * Cascade-delete a unit (`delete_unit_cascade` RPC — soft-deletes the unit
   * and its dependent rows atomically in Postgres). Company-scoped via
   * p_company_id; throws on error.
   */
  async deleteCascade(unitId: string, companyId: string): Promise<void> {
    const { error } = await supabase.rpc("delete_unit_cascade", {
      p_unit_id: unitId,
      p_company_id: companyId,
    });
    if (error) throw error;
  },

  /**
   * Units owned at UNIT level by any of a person's owner-row fragments
   * (multi-owner properties — owner-details portfolio). Company-scoped;
   * throws on error. KEPT on Supabase.
   */
  async listOwnedByOwnerIds(companyId: string, ownerIds: string[]) {
    const { data, error } = await supabase
      .from(TABLE)
      .select("id, owner_id, property_id, unit_number, status, rent_amount")
      .eq("company_id", companyId)
      .in("owner_id", ownerIds);
    if (error) throw error;
    return data ?? [];
  },

  /**
   * `{ property_id }` of unit-level-owned units (non-null parents only) for a
   * person's owner-row fragments (owner financial summary). Company-scoped;
   * throws on error.
   */
  async listPropertyIdsByOwnerIds(
    companyId: string,
    ownerIds: string[],
  ): Promise<{ property_id: string | null }[]> {
    const { data, error } = await supabase
      .from(TABLE)
      .select("property_id")
      .eq("company_id", companyId)
      .in("owner_id", ownerIds)
      .not("property_id", "is", null);
    if (error) throw error;
    return data ?? [];
  },

  /**
   * HEAD-only count of ALL units in the company (occupancy denominator).
   * KEPT on Supabase — batched finance read. NEVER throws: the
   * pre-consolidation sites destructured `{ count }` only (a failed read
   * charts 0% occupancy), so an error resolves to null.
   */
  async count(companyId: string): Promise<number | null> {
    const { count } = await supabase
      .from(TABLE)
      .select("*", { count: "exact", head: true })
      .eq("company_id", companyId);
    return count ?? null;
  },

  /**
   * HEAD-only count of RENTED units in the company (occupancy numerator).
   * Same never-throws contract as `count()`.
   */
  async countRented(companyId: string): Promise<number | null> {
    const { count } = await supabase
      .from(TABLE)
      .select("*", { count: "exact", head: true })
      .eq("company_id", companyId)
      .eq("status", "rented");
    return count ?? null;
  },

  /**
   * Fire-and-forget AR↔EN auto-translation for a unit's description field.
   * NEVER throws — the unit has already been written by the caller, so a
   * translation outage must not roll back the save. Idempotent server-side.
   */
  async translate(id: string): Promise<void> {
    await fireAndForgetNestPost(API_ROUTES.units.translate(id), "units.translate");
  },

  /**
   * The narrow unit row for the task property-context sidebar (fe-tail T9 —
   * useTaskPropertyContext), including the legacy inline key columns the hook
   * synthesizes a unitSecurityKey from. NEVER throws — the inline original
   * destructured `{ data }` only.
   */
  async taskContextUnit(
    unitId: string,
    companyId: string,
  ): Promise<TaskContextUnitRow | null> {
    const { data } = await supabase
      .from(TABLE)
      .select(
        "id, unit_number, floor_number, bedrooms, bathrooms, key_type, key_number, amenities, rent_amount, annual_rent, property_id, images",
      )
      .eq("id", unitId)
      .eq("company_id", companyId)
      .maybeSingle();
    return (data as TaskContextUnitRow | null) ?? null;
  },
};
